import Vue from 'vue'
import { FORM_ITEM_TYPE } from './components/index.js'
// 支持的表达式示例
const expressions = {
  // 基本比较
  basic: "values.field === 'value'",
  
  // 数组操作
  array: "Array.isArray(values.field) && values.field.length > 0",
  
  // 多条件组合
  multiple: "(values.field1 === 'value1' && values.field2 === 'value2') || values.field3 === 'value3'",
  
  // 数值比较
  number: "values.field > 100 && values.field < 200",
  
  // 包含判断
  includes: "values.field.includes('value')",
  
  // 类型判断
  type: "typeof values.field === 'string'"
};


// 1. 常量定义
const ALLOWED_EXPRESSIONS = [
    'eval', 'Function', 'new Function',
    'document', 'window', 'process',
    'require', 'import', 'export',
    'this', 'arguments'
];

// 2. 工具函数
function validateExpression(expression) {
    const hasInvalidOperator = !ALLOWED_EXPRESSIONS.every(allowed => 
        !expression.includes(allowed) || expression.includes(` ${allowed} `)
    );
    
    if (hasInvalidOperator) {
        console.warn('表达式包含不允许的操作符:', expression);
        return false;
    }
    
    try {
        new Function('values', `return ${expression}`);
        return true;
    } catch (error) {
        console.warn('表达式语法错误:', error);
        return false;
    }
}

// 3. 条件评估函数
export function evaluateCondition(condition, formData) {
    const { field, operator, value } = condition;
    const fieldValue = formData[field];

    switch (operator) {
        case '===': return fieldValue === value;
        case '!==': return fieldValue !== value;
        case '>': return fieldValue > value;
        case '<': return fieldValue < value;
        case '>=': return fieldValue >= value;
        case '<=': return fieldValue <= value;
        case 'includes': return Array.isArray(fieldValue) && fieldValue.includes(value);
        case 'not-includes': return Array.isArray(fieldValue) && !fieldValue.includes(value);
        case 'else': return true;
        default: return false;
    }
}

export function evaluateConditionGroup(group, formData) {
    const { operator, conditions } = group;
    if (!conditions || !Array.isArray(conditions)) return false;

    const results = conditions.map(condition => 
        'operator' in condition && 'conditions' in condition
            ? evaluateConditionGroup(condition, formData)
            : evaluateCondition(condition, formData)
    );

    return operator === 'and' ? results.every(Boolean) : results.some(Boolean);
}

function evaluateConditionGroups(groups, formData) {
    for (const group of groups) {
        const result = evaluateConditionGroup(group, formData);
        if (result) return { result: true, matchedGroup: group };
    }
    return { result: false };
}

// 4. 处理器定义
export const conditionHandlers = {
    expression: (values, expression) => {
        try {
            const safeValues = new Proxy(values, {
                get: (target, prop) => {
                    const value = target[prop];
                    if (value === null || value === undefined) {
                        return {
                            includes: () => false,
                            length: 0,
                            indexOf: () => -1,
                            find: () => undefined,
                            filter: () => [],
                            map: () => []
                        };
                    }
                    return value;
                }
            });
            const func = new Function('values', `return ${expression}`);
            return func(safeValues);
        } catch (error) {
            return false;
        }
    }
};

// 5. 主函数
export function createFormItem(formItemType, payload, next, parent) {
    if (FORM_ITEM_TYPE[formItemType] === undefined && !formItemType.startsWith('El')) {
        throw new Error(`Invalid form item type: ${formItemType}`);
    }

    if (payload.dependencies) {
        payload.effects = payload.effects || [];
        if (payload.effects.length) {
            payload.effects.forEach(effect => {
                if (effect.condition?.type === 'expression') {
                    if (!validateExpression(effect.condition.expression)) {
                        effect.condition.expression = 'false';
                    }
                }
            });
        }
    }

    const nextFunc = (current, acients) => {
        const formData = acients.reduce((acc, item) => {
            acc[item.payload.field] = item.payload.value;
            return acc;
        }, {});

        let nextItem = next(current, acients, formData);
        if (nextItem) {
            nextItem.parent = current;
        }
        return nextItem;
    };

    return {
        type: formItemType,
        payload: Vue.observable(payload),
        next: nextFunc,
        parent: parent || null
    };
} 

export const nodeItemData = Vue.observable({});